import React from 'react';
import { Link } from 'react-router-dom';

// ─────────────────────────────────────────────────────────────────────────
// FOOTER — site-wide navigation surface (s71)
// ─────────────────────────────────────────────────────────────────────────
//
// Extracted from Homepage.jsx, where it had lived inline since s63.5 and
// rendered on exactly one route. 439 of 440 pages had no footer at all —
// every tool, article, guide, state and city page was a dead end for both
// crawlers and readers. This component is rendered once in App.jsx, outside
// <Routes>, so it appears on every route including 404.
//
// ── Contract with scripts/verify-nav-routes.mjs ────────────────────────
//
// That script does NOT parse this file as JS — it reads the source text and
// walks the FOOTER_SECTIONS literal below with a bracket matcher, anchored on
// the `const FOOTER_SECTIONS = [` declaration and each `['<HEADING>',` inside it.
//
// Consequences, all load-bearing:
//   • Sections must stay as `['<HEADING>', [ ... ]]` array literals.
//   • Links must stay as `['<Label>', '<path>']` pairs on single lines,
//     single-quoted. A template literal, a constant reference, a computed
//     path, or a prettier reflow that splits a pair across two lines all
//     parse as "link absent" and fail the gate.
//   • The `const FOOTER_SECTIONS = [` declaration line must keep that name.
//
// This is the same brittleness that left verify-no-orphans.mjs inert (see
// HANDOFF debt #5) — a text-matching guardrail silently unhooked by a
// refactor. Keeping the literal shape stable is cheaper than rewriting the
// verifier onto an AST, and the gate stays green rather than becoming
// another permanently-red script nobody reads.
//
// ── THE TOOLS: 22 of 23 ────────────────────────────────────────────────
//
// Streak Diagnostic is deliberately omitted — it's surfaced via the §01
// most-used tile on the homepage and via per-tool inbound links. The
// omission is asserted in verify-nav-routes.mjs (INTENTIONAL_TOOL_OMISSIONS),
// so a quiet swap for a different tool gets caught.
//
// ── Dark mode ──────────────────────────────────────────────────────────
//
// Every page owns its own `const [dark, setDark] = useState(false)` and its
// own ☀/☾ toggle. There is no shared theme context, so a globally-rendered
// footer cannot read the current page's toggle state. This renders light
// always, which matches the default first paint of every route — the
// mismatch only appears if a reader toggles dark, and only at the very
// bottom of the page.
//
// The `dark` prop is plumbed through and fully implemented so that lifting
// `dark` into a context later is a one-line change here rather than a
// rewrite. Doing that lift now would mean touching ~30 page components in a
// session that also ships 7 articles; it's logged in HANDOFF instead.

const FOOTER_SECTIONS = [
  ['THE REFERENCE', [
    ['Encyclopedia', '/encyclopedia'],
    ['Diagnostician', '/diagnostician'],
    ['Field Notes', '/field-notes'],
    ['Pro Track', '/pro-track'],
    ['Glossary', '/glossary'],
    ['Blog', '/blog'],
  ]],
  ['THE TOOLS', [
    ['Hard Water Scorer', '/tools/hard-water-scorer'],
    ['Solution Calculator', '/tools/solution-calculator'],
    ['Technique Library', '/tools/technique-library'],
    ['Cost Estimator', '/tools/cost-estimator'],
    ['Tint Identifier', '/tools/tint-identifier'],
    ['Bird-Soiling Urgency', '/tools/bird-soiling-urgency'],
    ['Cleaning Schedule Builder', '/tools/cleaning-schedule-builder'],
    ['Cold-Weather Calculator', '/tools/cold-weather-window-calculator'],
    ['Drying Window Predictor', '/tools/drying-window-predictor'],
    ['Frame Substrate Identifier', '/tools/frame-substrate-identifier'],
    ['Hard-Water Stain Triage', '/tools/hard-water-stain-removal'],
    ['Heritage Glass Triage', '/tools/heritage-glass-triage'],
    ['IGU Self-Check', '/tools/igu-self-check'],
    ['Ladder Safety Calculator', '/tools/ladder-safety-calculator'],
    ['Pole Reach Calculator', '/tools/pole-reach-calculator'],
    ['Pure-Water TDS Diagnostic', '/tools/pure-water-tds-diagnostic'],
    ['Regional Protocol Generator', '/tools/regional-protocol-generator'],
    ['Salt-Spray Interval', '/tools/salt-spray-interval-calculator'],
    ['Scratch Risk Calculator', '/tools/scratch-risk-calculator'],
    ['Solvent Ladder Selector', '/tools/solvent-ladder-selector'],
    ['Squeegee Rubber Tracker', '/tools/squeegee-rubber-tracker'],
    ['Storefront Frequency', '/tools/storefront-frequency'],
  ]],
  ['BY REGION', [
    ['All 50 states', '/states'],
    ['The Water Atlas', '/water'],
    ['Find a cleaner', '/find-a-cleaner'],
    ['California guide', '/states/california'],
    ['Texas guide', '/states/texas'],
    ['Florida guide', '/states/florida'],
    ['New York guide', '/states/new-york'],
  ]],
  ['ABOUT', [
    ['Editorial Standards', '/editorial-standards'],
    ['Methodology', '/methodology'],
    ['Corrections', '/corrections'],
    ['Contributors', '/contributors'],
    ['Sources', '/sources'],
    ['Contact', '/contact'],
    ['Submit a Field Note', '/submit'],
  ]],
];

export default function Footer({ dark = false }) {
  const T = dark
    ? { bg: '#1A1D1C', inkSoft: '#B8B2A4', inkFaint: '#6B6960',
        rule: '#2E3236', sienna: '#D67148', ink: '#F4F1EA' }
    : { bg: '#F4F1EA', inkSoft: '#5A5A52', inkFaint: '#A39E91',
        rule: '#D9D3C5', sienna: '#B5532A', ink: '#1A1D1C' };

  const fontDisplay = "'Fraunces', 'Times New Roman', serif";
  const fontBody = "'Inter Tight', sans-serif";
  const fontMono = "'JetBrains Mono', monospace";

  // Scoped hover class. The site's existing `.ghost-link` rule is declared
  // per-page inside each page's own <style> block, so a footer depending on
  // it would lose its hover on any route that doesn't happen to declare it.
  // Own class, own rule, no cross-file dependency.
  return (
    <footer style={{
      background: T.bg,
      borderTop: `1px solid ${T.rule}`,
      color: T.ink,
      fontFamily: fontBody,
    }}>
      <style>{`
        .wwg-footer-link {
          transition: color 0.15s ease;
        }
        .wwg-footer-link:hover {
          color: ${T.sienna};
        }
      `}</style>

      <div style={{
        maxWidth: 1080, margin: '0 auto', padding: '48px 28px 0',
      }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: 40, paddingBottom: 48,
          borderBottom: `1px solid ${T.rule}`,
        }}>
          <div>
            <div style={{
              fontFamily: fontDisplay, fontSize: 22, marginBottom: 12,
            }}>
              Window Washing <em style={{ fontStyle: 'italic' }}>Guide</em>
            </div>
            <p style={{
              fontFamily: fontDisplay, fontSize: 15,
              color: T.inkSoft, lineHeight: 1.55, margin: 0,
            }}>
              A working reference for the chemistry, technique, and quiet obsession of cleaning glass.
            </p>
          </div>

          {FOOTER_SECTIONS.map(([heading, links]) => (
            <div key={heading}>
              <div style={{
                fontFamily: fontMono, fontSize: 11,
                letterSpacing: '0.18em', color: T.sienna,
                marginBottom: 16,
              }}>
                {heading}
              </div>
              {links.map(([label, path]) => (
                <div key={label} style={{ marginBottom: 8 }}>
                  <Link to={path} className="wwg-footer-link" style={{
                    fontFamily: fontBody, fontSize: 14,
                    color: T.inkSoft, textDecoration: 'none',
                  }}>
                    {label}
                  </Link>
                </div>
              ))}
            </div>
          ))}
        </div>

        <div className="wwg-footer-legal" style={{
          display: 'flex', justifyContent: 'space-between',
          paddingTop: 24, paddingBottom: 24,
          fontFamily: fontMono, fontSize: 11,
          letterSpacing: '0.1em', color: T.inkFaint,
        }}>
          <span>© 2026 Window Washing Guide</span>
          <span>Made with patience · Built for the long haul</span>
        </div>
      </div>
    </footer>
  );
}
